import React from "react";
import PropTypes from "prop-types";
import FileSaver from "file-saver";
import { CircularProgress, Grid, Typography } from "@material-ui/core";
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@material-ui/icons";
import moment from "moment";
import SectionContentWrapper from "../../../app/components/SectionContentWrapper";
import { translate as t } from "../../translate/localization";
import useStyles from "./styles";
import CobeeButton from "../../../app/components/CobeeButton";
import { error, success } from "../../../assets/variables.module.scss";
import { MonetaryAmount } from "../../../app/domain/monetary-amount";
import { downloadFile } from "../../repository/public-requests-repository";
import approveRentingRequest from "../../../usecases/requests/approve-renting-request";
import denyRentingRequest from "../../../usecases/requests/deny-renting-request";
import EmptyDataForTableMessage from "../../../app/components/modals/employee/DeleteEmployeesModal/components/EmptyDataForTableMessage";
import { showErrorMessage } from "../../../usecases/commons/show-error-message";

export default function EmployeeDetailRequestList({ isLoading, requests }) {
  const {
    spinner,
    requestTypeContainer,
    typeTitleContainer,
    requestContainer,
    fileIcon,
    requestTitle,
    approvalIcon,
    approvedIcon,
    rejectedIcon,
    emptyListContainer,
  } = useStyles();

  const formatDate = (date) =>
    moment(new Date(date) * 1000).format("DD/MM/YYYY");

  const handleDownload = async (title, fileId) => {
    try {
      const { data } = await downloadFile({ fileId });
      FileSaver(data, title);
    } catch (e) {
      showErrorMessage(t("common-messages.basic-error-message"));
    }
  };

  return (
    <SectionContentWrapper title={t("employees-section.renting-requests")}>
      <Grid item xs={12} container justify="center">
        {isLoading ? (
          <CircularProgress className={spinner} />
        ) : (
          <Grid item container xs={12}>
            {requests.length > 0 ? (
              <Grid item container xs={12}>
                {requests.filter(({ approved }) => approved === null).length >
                  0 && (
                  <Grid item container xs={12} className={requestTypeContainer}>
                    <Grid item container xs={12} className={typeTitleContainer}>
                      <Typography variant="body2">
                        {t("common-messages.pending-plural")}
                      </Typography>
                    </Grid>
                    {requests
                      .filter(({ approved }) => approved === null)
                      .map(({ requestId, description, stepAnswers }) => (
                        <Grid
                          key={requestId}
                          item
                          container
                          xs={12}
                          className={requestContainer}
                        >
                          <Grid
                            item
                            container
                            xs={12}
                            justify="space-between"
                            alignItems="center"
                          >
                            <Typography variant="h5">
                              <b>{description}</b>
                            </Typography>
                            <Grid
                              item
                              xs={6}
                              container
                              justify="flex-end"
                              alignItems="center"
                            >
                              <CobeeButton
                                styleType="uncontained"
                                iconColor={success}
                                onClick={() =>
                                  approveRentingRequest({ requestId })
                                }
                              >
                                <CheckCircleIcon />
                                <Typography variant="body2">
                                  {t("actions.approve-request")}
                                </Typography>
                              </CobeeButton>
                              <CobeeButton
                                styleType="uncontained"
                                iconColor={error}
                                onClick={() =>
                                  denyRentingRequest({ requestId })
                                }
                              >
                                <CancelIcon />
                                <Typography variant="body2">
                                  {t("actions.reject-request")}
                                </Typography>
                              </CobeeButton>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid item>
                              {stepAnswers
                                .filter(({ file }) => file)
                                .map(({ stepId, file }) => (
                                  <CobeeButton
                                    styleType="uncontained"
                                    key={stepId}
                                    onClick={async () => {
                                      await handleDownload(
                                        file.filename,
                                        file.fileId
                                      );
                                    }}
                                  >
                                    <DescriptionIcon className={fileIcon} />
                                    <Typography variant="body2">
                                      {file.filename || "-"}
                                    </Typography>
                                  </CobeeButton>
                                ))}
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                  </Grid>
                )}
                {requests.filter(({ approved }) => approved !== null).length >
                  0 && (
                  <Grid item container xs={12}>
                    <Grid item container xs={12} className={typeTitleContainer}>
                      <Typography variant="body2">
                        {t("common-messages.resolved-plural")}
                      </Typography>
                    </Grid>
                    {requests
                      .filter(({ approved }) => approved !== null)
                      .map(
                        ({
                          requestId,
                          description,
                          approved,
                          rejectionReason,
                          createdAtTimestamp,
                          details,
                          summary,
                        }) => (
                          <Grid
                            item
                            container
                            xs={12}
                            key={requestId}
                            className={requestContainer}
                          >
                            <Grid
                              item
                              container
                              xs={12}
                              className={requestTitle}
                            >
                              <Typography variant="h5">
                                <b>{description}</b>
                              </Typography>
                            </Grid>
                            <Grid item container xs={12}>
                              <Grid
                                item
                                xs={approved ? 2 : 8}
                                container
                                direction="column"
                              >
                                <Typography variant="body2">
                                  {t("common-messages.request-date")}:
                                </Typography>
                                <Typography variant="body2">
                                  {formatDate(createdAtTimestamp)}
                                </Typography>
                              </Grid>
                              {approved && (
                                <Grid item xs={6} container>
                                  <Grid
                                    item
                                    xs={4}
                                    container
                                    direction="column"
                                  >
                                    <Typography variant="body2">
                                      {t("common-messages.first-quota-date")}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatDate(
                                        summary?.firstPaymentTimestamp
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={4}
                                    container
                                    direction="column"
                                  >
                                    <Typography variant="body2">
                                      {t("common-messages.quotas-amount")}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {summary?.amountPerPayment &&
                                        MonetaryAmount.ofCurrency(
                                          summary?.amountPerPayment
                                        ).prettifyMoney({})}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={4}
                                    container
                                    direction="column"
                                  >
                                    <Typography variant="body2">
                                      {t("common-messages.number-of-quotas")}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {details?.numberOfPayments}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              )}
                              <Grid item xs={4} container>
                                <Grid
                                  item
                                  xs={12}
                                  container
                                  alignItems="center"
                                >
                                  {approved ? (
                                    <CheckCircleIcon
                                      className={`${approvalIcon} ${approvedIcon}`}
                                    />
                                  ) : (
                                    <CancelIcon
                                      className={`${approvalIcon} ${rejectedIcon}`}
                                    />
                                  )}
                                  <Typography variant="body2">
                                    <b>
                                      {t(
                                        `common-messages.${
                                          approved ? "accepted" : "rejected"
                                        }-female`
                                      )}
                                    </b>
                                  </Typography>
                                </Grid>
                                {approved && summary ? (
                                  <Grid
                                    item
                                    xs={12}
                                    container
                                    direction="column"
                                  >
                                    <Typography variant="body2">
                                      {t("common-messages.first-quota-amount")}:{" "}
                                      {summary.firstPaymentAmount &&
                                        MonetaryAmount.ofCurrency(
                                          summary.firstPaymentAmount
                                        ).prettifyMoney({})}
                                    </Typography>
                                    <Typography variant="body2">
                                      {t(
                                        "common-messages.amount-to-extract-from-payroll"
                                      )}
                                      :{" "}
                                      {summary.monthlyAttribution &&
                                        MonetaryAmount.ofCurrency(
                                          summary.monthlyAttribution
                                        ).prettifyMoney({})}
                                    </Typography>
                                    <Typography variant="body2">
                                      {t(
                                        "request-section.renting-total-amount"
                                      )}
                                      :{" "}
                                      {summary.totalAmount &&
                                        MonetaryAmount.ofCurrency(
                                          summary.totalAmount
                                        ).prettifyMoney({})}
                                    </Typography>
                                  </Grid>
                                ) : (
                                  <Grid item xs={12} container>
                                    <Typography variant="body2">
                                      {rejectionReason || "..."}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        )
                      )}
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid
                item
                className={emptyListContainer}
                container
                justify="center"
              >
                <EmptyDataForTableMessage
                  message={t("common-messages.empty-employee-requests-data")}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </SectionContentWrapper>
  );
}

EmployeeDetailRequestList.defaultProps = {
  isLoading: false,
  requests: [],
};

EmployeeDetailRequestList.propTypes = {
  isLoading: PropTypes.bool,
  requests: PropTypes.array,
};
